import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Check, X, ExternalLink, RefreshCw, Database, ShieldCheck, Download, Flag } from 'lucide-react';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { devError } from '../utils/logger';
import { toast } from 'sonner';
import { Navigate, Link } from 'react-router-dom';

// Allowlist of platform-admin emails — kept in sync with backend
// `ADMIN_EMAILS` env var. Add more entries as the team grows.
const ADMIN_EMAILS = ['thisisradas@gmail.com'];

/**
 * Admin-only moderation queue for community-submitted inspiration projects.
 *
 * Reachable at /admin/inspirations. Auth-gated client-side and server-side.
 */
export default function AdminInspirationsPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);    // slug currently being acted on
  const [rejectReason, setRejectReason] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/inspirations/admin/pending`);
      setItems(data.items || []);
    } catch (e) {
      devError('admin-pending', e);
      toast.error(e.response?.data?.detail || 'Failed to load pending submissions');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = !!user && (user.user_type === 'admin' || (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())));

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [isAdmin]);

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-accent animate-spin" />
      </div>
    );
  }
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const moderate = async (slug, action) => {
    setActing(slug);
    try {
      await axios.post(`${API}/inspirations/admin/${slug}/moderate`, {
        action,
        reason: action === 'reject' ? (rejectReason[slug] || null) : null,
      });
      toast.success(action === 'approve' ? 'Approved — now live in the gallery' : 'Rejected');
      setItems((prev) => prev.filter((p) => p.slug !== slug));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="admin-inspirations-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Moderation queue</p>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-2">
              Inspiration submissions
            </h1>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading…' : `${items.length} project${items.length === 1 ? '' : 's'} awaiting review`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/suppliers">
              <Button variant="outline" data-testid="admin-suppliers-link">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Supplier queue
              </Button>
            </Link>
            <Link to="/admin/imports">
              <Button variant="outline" data-testid="admin-imports-link">
                <Download className="w-4 h-4 mr-2" />
                External imports
              </Button>
            </Link>
            <Link to="/admin/reports">
              <Button variant="outline" data-testid="admin-reports-link">
                <Flag className="w-4 h-4 mr-2" />
                Reports
              </Button>
            </Link>
            <ReseedProductionButton />
            <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-accent animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 rounded-xl border border-border/20 bg-card/30">
            <p className="text-muted-foreground">All caught up — nothing pending.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="admin-pending-list">
            {items.map((p) => (
              <article
                key={p.slug}
                className="rounded-xl border border-border/30 bg-card/30 overflow-hidden"
                data-testid={`pending-${p.slug}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-0">
                  {/* Image rail */}
                  <div className="bg-black/30 p-3 grid grid-cols-2 gap-2 content-start">
                    {(p.images || []).slice(0, 4).map((img, idx) => (
                      <img
                        key={img.url || idx}
                        src={img.url}
                        alt=""
                        className="w-full h-24 object-cover rounded"
                        loading="lazy"
                      />
                    ))}
                    {(p.images || []).length === 0 && (
                      <p className="col-span-2 text-xs text-muted-foreground italic">No images</p>
                    )}
                    {(p.images || []).length > 4 && (
                      <p className="col-span-2 text-[10px] text-muted-foreground text-center">
                        +{p.images.length - 4} more
                      </p>
                    )}
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    <h2 className="font-playfair text-xl font-semibold mb-1">{p.title}</h2>
                    <p className="text-xs text-muted-foreground mb-3">
                      {p.architect && <>Architect: <span className="text-foreground/80">{p.architect}</span></>}
                      {p.architect && p.photographer && ' · '}
                      {p.photographer && <>Photo: <span className="text-foreground/80">{p.photographer}</span></>}
                    </p>
                    {p.body && <p className="text-sm text-foreground/80 mb-3">{p.body}</p>}

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(p.categories || []).map((c) => (
                        <span key={c} className="text-[10px] uppercase px-2 py-0.5 rounded bg-accent/15 text-accent border border-accent/30">
                          {c}
                        </span>
                      ))}
                      {(p.tags || []).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-border/30 text-muted-foreground">
                          #{t}
                        </span>
                      ))}
                    </div>

                    <div className="text-xs text-muted-foreground mb-4 space-y-0.5">
                      {p.source_url && (
                        <p>
                          <a href={p.source_url} target="_blank" rel="noreferrer" className="text-accent hover:underline inline-flex items-center gap-1">
                            Source link <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                      )}
                      {p.submitter_name && <p>Submitted by: {p.submitter_name}</p>}
                      {p.submitter_email && <p>Email: {p.submitter_email}</p>}
                    </div>

                    <Textarea
                      value={rejectReason[p.slug] || ''}
                      onChange={(e) => setRejectReason((r) => ({ ...r, [p.slug]: e.target.value }))}
                      placeholder="Optional rejection reason (private to you)"
                      rows={2}
                      className="mb-3 text-xs"
                      data-testid={`reject-reason-${p.slug}`}
                    />

                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={() => moderate(p.slug, 'approve')}
                        disabled={acting === p.slug}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                        data-testid={`approve-${p.slug}`}
                      >
                        {acting === p.slug ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        Approve
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => moderate(p.slug, 'reject')}
                        disabled={acting === p.slug}
                        data-testid={`reject-${p.slug}`}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One-click re-seed of the canonical inspiration + listings + suppliers data.
// Useful after a fresh production deploy when the DB came up empty or stale.
// ---------------------------------------------------------------------------
function ReseedProductionButton() {
  const [seeding, setSeeding] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const reseed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/seed-production?overwrite=true`);
      const c = data.current_counts || {};
      toast.success(
        `Re-seeded — ${c.listings || 0} listings, ${c.users || 0} users, ${c.inspirations || 0} inspirations`,
        { duration: 6000 },
      );
      // Reload after a beat so the updated content shows.
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-seed failed');
    } finally {
      setSeeding(false);
      setConfirmOpen(false);
    }
  };

  if (confirmOpen) {
    return (
      <div className="inline-flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Wipe & re-seed?</span>
        <Button
          size="sm"
          onClick={reseed}
          disabled={seeding}
          className="bg-red-600 hover:bg-red-500 text-white"
          data-testid="reseed-confirm"
        >
          {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
          Yes, re-seed
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setConfirmOpen(false)} disabled={seeding}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="outline"
      onClick={() => setConfirmOpen(true)}
      data-testid="reseed-trigger"
      title="Wipes listings/users/inspirations and reloads from production_data_export.json"
    >
      <Database className="w-4 h-4 mr-2" />
      Re-seed production data
    </Button>
  );
}
